import { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";

const LINES = [
  "BOOT · yabbai-core v5",
  "linking treasury telemetry … stripe channel",
  "spinning up crew · 18 agents · 4 divisions",
  "arming approval gate · human-in-the-loop",
  "command deck ready",
];

/* One-time boot animation. Plays once per browser session and is fully skipped
   for users who prefer reduced motion. */
export default function BootSequence({ onDone }) {
  const reduce = useReducedMotion();
  const alreadyBooted = useMemo(() => {
    try {
      return sessionStorage.getItem("yb_booted") === "1";
    } catch {
      return false;
    }
  }, []);

  const [show, setShow] = useState(!alreadyBooted && !reduce);
  const [step, setStep] = useState(0);

  useEffect(() => {
    if (!show) {
      onDone?.();
      return;
    }
    try { sessionStorage.setItem("yb_booted", "1"); } catch { /* ignore */ }
    let i = 0;
    const tick = setInterval(() => {
      i += 1;
      setStep(i);
      if (i >= LINES.length) {
        clearInterval(tick);
        setTimeout(() => {
          setShow(false);
          onDone?.();
        }, 550);
      }
    }, 360);
    return () => clearInterval(tick);
  }, [show, onDone]);

  if (alreadyBooted || reduce) return null;

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          className="fixed inset-0 z-[100] flex items-center justify-center hud-grid"
          style={{ background: "var(--yb-bg-2)" }}
          initial={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.5 }}
          aria-hidden="true"
        >
          <div className="w-[min(560px,90vw)] px-6">
            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              className="font-display text-[clamp(28px,7vw,52px)] tracking-[0.12em] text-center text-glow"
              style={{ color: "var(--yb-cyan)" }}
            >
              YABBAI
            </motion.div>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.2 }}
              className="font-head text-center tracking-[0.5em] text-[12px] mb-7"
              style={{ color: "var(--yb-text-dim)" }}
            >
              ORCHESTRA
            </motion.div>

            <div className="space-y-2 font-mono text-[12px]">
              {LINES.map((line, idx) => (
                <motion.div
                  key={line}
                  initial={{ opacity: 0, x: -8 }}
                  animate={idx < step ? { opacity: 1, x: 0 } : { opacity: 0.2, x: -8 }}
                  className="flex items-center gap-2"
                  style={{ color: idx < step ? "var(--yb-mint)" : "var(--yb-text-muted)" }}
                >
                  <span style={{ color: "var(--yb-cyan)" }}>{idx < step ? "✓" : "▸"}</span>
                  <span>{line}</span>
                </motion.div>
              ))}
            </div>

            <div className="mt-7 h-1 rounded-full overflow-hidden" style={{ background: "var(--yb-panel-3)" }}>
              <motion.div
                className="h-full"
                style={{ background: "var(--yb-cyan)", boxShadow: "0 0 16px var(--yb-cyan)" }}
                initial={{ width: "0%" }}
                animate={{ width: `${(step / LINES.length) * 100}%` }}
                transition={{ ease: "easeOut" }}
              />
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
